<template>
</template>

<script>
import {
  addNativeTrackingStatusListener,
  getNativeTrackingStatus,
  isNativeTrackingAvailable,
  startNativeTracking
} from '@/services/nativeTracking';
import { trackingLocationUrl } from '@/config/endpoints';
import { createWebTracking } from '@/services/webTracking';

export default {
  name: "Rastreamento",
  data() {
    return {
      bloqueadoPorErro: false,
      batteryManager: null,
      webTracking: null,
      nativeStatusListener: null,
      lastNativeErrorTimestamp: null,
      nativeStartPromise: null
    }
  },

  methods: {
    iniciarRastreamentoWeb() {
      if (!this.webTracking) {
        this.criarRastreamentoWeb();
      }

      this.webTracking.start();
    },

    sincronizarRastreamentoWeb() {
      if (document.visibilityState === 'visible' && !this.bloqueadoPorErro) {
        this.iniciarRastreamentoWeb();
      } else {
        this.webTracking?.pause();
      }
    },

    onVisibilityChange() {
      if (isNativeTrackingAvailable()) {
        if (document.visibilityState === 'visible') {
          this.garantirRastreamentoNativo();
        }
        return;
      }

      this.sincronizarRastreamentoWeb();
    },

    criarRastreamentoWeb() {
      this.webTracking = createWebTracking({
        sendPosition: position => this.salvarPosicao(position),
        onGpsStatus: available => {
          this.$store.state.geoLocation = available;
        },
        onServerStatus: (available, error) => {
          this.$store.state.trackingServer = available;

          if (!available && error) {
            this.registrarErro(error, 'server');
          }
        },
        onStatusChange: status => {
          Object.assign(this.$store.state.tracking, status);
        },
        onError: error => this.tratarErro(error),
        intervalMs: 10000,
        minDistanceMeters: 15,
        heartbeatMs: 60000
      });
    },

    garantirRastreamentoNativo() {
      if (this.nativeStartPromise) return this.nativeStartPromise;

      this.nativeStartPromise = this.iniciarRastreamentoNativo()
        .finally(() => {
          this.nativeStartPromise = null;
        });

      return this.nativeStartPromise;
    },

    async iniciarRastreamentoNativo() {
      try {
        this.$store.state.trackingServer = null;
        await this.configurarStatusNativo();

        Object.assign(this.$store.state.tracking, {
          mode: 'native',
          active: true
        });

        return await startNativeTracking({
          url: trackingLocationUrl,
          xAuth: localStorage.getItem('x_auth') || '',
          intervalSeconds: 15,
          minDistanceMeters: 15,
          heartbeatSeconds: 60
        });
      } catch (error) {
        const trackingBlocked = [
          'LOCATION_PERMISSION_DENIED',
          'PRECISE_LOCATION_REQUIRED',
          'NOTIFICATION_PERMISSION_DENIED',
          'LOCATION_SERVICES_DISABLED'
        ].includes(error.code);

        this.$store.state.geoLocation = false;
        Object.assign(this.$store.state.tracking, {
          active: false,
          gpsStatus: ['LOCATION_PERMISSION_DENIED', 'PRECISE_LOCATION_REQUIRED'].includes(error.code) ? 'denied' : 'unavailable',
          lastError: {
            message: error.message || 'Nao foi possivel iniciar o rastreamento nativo.',
            code: error.code || null,
            timestamp: Date.now()
          }
        });

        if (trackingBlocked) {
          localStorage.removeItem('geoLocation');

          this.$emit('location-blocked');

          return null;
        }

        this.$swal(error.message || 'Nao foi possivel iniciar o rastreamento nativo.');
        return null;
      }
    },

    async configurarStatusNativo() {
      if (!this.nativeStatusListener) {
        this.nativeStatusListener = await addNativeTrackingStatusListener(status => {
          this.aplicarStatusNativo(status);
        });
      }

      const status = await getNativeTrackingStatus();
      this.aplicarStatusNativo(status);
      return status;
    },

    aplicarStatusNativo(status) {
      if (!status) return;

      if (status.lastError?.timestamp && status.lastError.timestamp !== this.lastNativeErrorTimestamp) {
        this.lastNativeErrorTimestamp = status.lastError.timestamp;
        this.registrarErro(status.lastError, `native_${status.lastError.source || 'tracking'}`);
      }

      Object.assign(this.$store.state.tracking, {
        mode: 'native',
        active: !!status.running,
        gpsStatus: status.gpsStatus || null,
        serverStatus: status.serverStatus || null,
        lastCapturedPosition: status.lastCapturedPosition || null,
        lastCapturedAt: status.lastCapturedAt || null,
        lastSentPosition: status.lastSentPosition || null,
        lastSentAt: status.lastSentAt || null,
        pendingPosition: status.pendingPosition || null,
        retryAt: status.retryAt || null,
        lastError: status.lastError || null
      });

      this.$store.state.geoLocation = status.gpsStatus === 'available';
      this.$store.state.trackingServer = status.serverStatus === 'online'
        ? true
        : status.serverStatus === 'offline' ? false : null;
    },

    tratarErro(error) {
      let msgGeo = null;
      let shouldStop = false;

      switch (error.code) {
        case error.PERMISSION_DENIED:
          shouldStop = true;
          msgGeo = "Permissão negada para acessar a localização.";
          break;

        case error.POSITION_UNAVAILABLE:
          msgGeo = "Localização indisponível.";
          break;

        case error.TIMEOUT:
          msgGeo = "Tempo esgotado para obter a localização.";
          break;

        default:
          msgGeo = "Erro desconhecido ao acessar localização.";
      }

      this.registrarErro(error, 'gps');

      if (shouldStop) {
        this.bloqueadoPorErro = true;
        localStorage.removeItem('geoLocation');
        this.webTracking?.pause();

        this.$emit('location-blocked');
      }
    },

    registrarErro(error, source) {
      let log;

      try {
        log = JSON.parse(localStorage.getItem('errorLog') || '[]');
      } catch (parseError) {
        log = [];
      }

      log.push({
        source,
        msg: error?.body?.message || error?.message || error?.statusText || 'Erro no rastreamento.',
        code: error?.code || error?.status || null,
        timestamp: Date.now()
      });
      localStorage.setItem('errorLog', JSON.stringify(log.slice(-50)));
    },

    async salvarPosicao(position) {
      const { latitude, longitude, accuracy, altitude, speed, heading } = position.coords;
      const xAuth = localStorage.getItem('x_auth') || '';
      const battery = await this.getBatteryData();
      const payload = {
        latitude,
        longitude,
        accuracy_m: accuracy ?? null,
        altitude_m: altitude ?? null,
        speed_mps: speed ?? null,
        bearing_deg: heading ?? null,
        captured_at: new Date(position.timestamp || Date.now()).toISOString(),
        source: 'web',
        battery,
        mocked: null,
        provider: null
      };

      return this.$http.post(trackingLocationUrl, payload, {
        emulateJSON: false,
        headers: {
          Authorization: `Bearer ${xAuth}`,
          'Content-Type': 'application/json'
        }
      });
    },

    async getBatteryData() {
      if (!navigator.getBattery) {
        return null;
      }

      try {
        this.batteryManager = this.batteryManager || await navigator.getBattery();
        return {
          level_percent: Math.round(this.batteryManager.level * 100),
          charging: this.batteryManager.charging
        };
      } catch (error) {
        return null;
      }
    }
  },

  mounted() {
    const enabled = localStorage.getItem('key') && localStorage.getItem('geoLocation');
    if (!enabled) return;

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (isNativeTrackingAvailable()) {
      this.garantirRastreamentoNativo();
      return;
    }

    if (!navigator.geolocation) return;

    this.sincronizarRastreamentoWeb();
  },

  beforeDestroy() {
    this.webTracking?.destroy();
    this.nativeStatusListener?.remove();
    this.nativeStatusListener = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}
</script>

<style scoped>

</style>
